// src/HeroPage/BestSellers.tsx
import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  ButtonBase,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ThreeSixtyIcon from "@mui/icons-material/ThreeSixty";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper/modules";
import "swiper/css";

import DiscoverCampaigns from "./DiscoverCampaigns";

import { useQuery } from "@apollo/client";
import { BEST_SELLERS_PREVIEW_QUERY } from "../graphql/products";
import { buildImageUrl } from "../config";
import { useNavigate } from "react-router-dom";

type BackendProductPreview = {
  id: string;
  slug?: string | null;
  customSchema?: string | null;
  altText?: string | null;
  name: string;
  productType: string;
  price: number;
  primaryImage?: string | null;
};

type BestSellersPreviewData = {
  bestSellersPreview: BackendProductPreview[];
};

type BackendProduct = {
  id: string;
  name: string;
  altText: string;
  slug?: string | null;
  customSchema?: string | null;
  productType: string;
  shortDescription?: string | null;
  price: number;
  galleryImages?: string[] | null;
  rotationImages?: string[] | null;
};

type Image360Props = {
  frames: string[];
  active: boolean;
  onClose: (e: any) => void;
  altText: string;
};

const Image360: React.FC<Image360Props> = ({
  frames,
  active,
  onClose,
  altText,
}) => {
  const [idx, setIdx] = React.useState(0);
  const lastXRef = React.useRef<number | null>(null);

  const clamp = (n: number) => {
    const len = frames.length || 1;
    return ((n % len) + len) % len;
  };

  const handleEnter = (x: number) => {
    lastXRef.current = x;
  };

  const handleMove = (x: number) => {
    if (lastXRef.current == null) return;
    const delta = x - lastXRef.current;
    const step =
      Math.sign(delta) * Math.max(1, Math.floor(Math.abs(delta) / 5));

    if (step !== 0) {
      setIdx((i) => clamp(i + step));
      lastXRef.current = x;
    }
  };

  const onMouseEnter = (e: React.MouseEvent) =>
    active && handleEnter(e.clientX);

  const onMouseMove = (e: React.MouseEvent) => active && handleMove(e.clientX);

  const onMouseLeave = () => (lastXRef.current = null);

  const onTouchStart = (e: React.TouchEvent) =>
    active && handleEnter(e.touches[0].clientX);

  const onTouchMove = (e: React.TouchEvent) =>
    active && handleMove(e.touches[0].clientX);

  const onTouchEnd = () => (lastXRef.current = null);

  const safeFrames = frames.length > 0 ? frames : [""];
  const currentFrame = active ? safeFrames[idx] : safeFrames[0];

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
        border: "1px solid #eee",
        aspectRatio: "4 / 3",
      }}
    >
      <Box
        component="img"
        loading="lazy"
        decoding="async"
        src={currentFrame}
        alt={altText}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          backgroundColor: "#fff",
        }}
      />

      {active && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose(e);
          }}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(255,255,255,0.95)",
            border: "1px solid #eaeaea",
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

/* -----------------------------
   Card
-------------------------------- */
type ProductCardProps = {
  product: BackendProduct;
  frames: string[];
  active: boolean;
  onActivate360: (e: any) => void;
  onDeactivate360: (e: any) => void;
  onOpenProduct: () => void;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  frames,
  active,
  onActivate360,
  onDeactivate360,
  onOpenProduct,
}) => {
  const price = `${product.price.toLocaleString()}`;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpenProduct}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpenProduct();
      }}
      sx={{ width: "100%", display: "block", textAlign: "left" }}
    >
      <Box sx={{ position: "relative" }}>
        {!active && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
            }}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: "#fff",
              border: "1px solid #eaeaea",
            }}
          >
            <FavoriteBorderIcon fontSize="small" />
          </IconButton>
        )}
        <Image360
          frames={frames}
          active={active}
          onClose={onDeactivate360}
          altText={(product as any).altText || product.name}
        />
        {/* uncomment once we have 360 images */}
        {/* {!active && frames.length > 1 && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onActivate360(e);
            }}
            sx={{
              position: "absolute",
              left: 12,
              bottom: 12,
              cursor: "pointer",
            }}
          >
            <Chip
              icon={<ThreeSixtyIcon sx={{ fontSize: 16, color: "#222" }} />}
              label="360°"
              size="small"
              sx={{
                bgcolor: "#fff",
                color: "#222",
                border: "1px solid #e5e5e5",
                height: 26,
              }}
            />
          </Box>
        )} */}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Typography
          sx={{
            fontFamily: "Playfair Display, serif",
            fontWeight: 500,
            fontSize: 18,
          }}
        >
          {product.name}
        </Typography>

        <Typography sx={{ mt: 1, fontWeight: 700, fontSize: 16 }}></Typography>
      </Box>
    </Box>
  );
};

/* -----------------------------
   Main
-------------------------------- */
const BestSellers: React.FC = () => {
  const swiperRef = React.useRef<any>(null);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const { data, loading } = useQuery<BestSellersPreviewData>(
    BEST_SELLERS_PREVIEW_QUERY,
    {
      variables: { limit: 15 },
      fetchPolicy: "cache-and-network",
    },
  );

  const products = data?.bestSellersPreview || [];

  return (
    <>
      <Box sx={{ backgroundColor: "#fff", py: { xs: 6, md: 10 } }}>
        {/* Heading */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: 1280,
            mx: "auto",
            px: { xs: 2, sm: 4, md: 6 },
            mb: { xs: 3, md: 5 },
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: "Playfair Display, serif",
              fontWeight: 500,
              fontSize: { xs: 28, md: 44 },
            }}
          >
            OUR BEST SELLERS
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => swiperRef.current?.slidePrev()}
              sx={{ border: "1px solid #e7e7e7", borderRadius: "50%" }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={() => swiperRef.current?.slideNext()}
              sx={{ border: "1px solid #e7e7e7", borderRadius: "50%" }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
          </Box>
        )}

        {/* Slider */}
        {!loading && products.length > 0 && (
          <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 1, sm: 3, md: 6 } }}>
            <Swiper
              modules={[Navigation, A11y]}
              onSwiper={(s) => (swiperRef.current = s)}
              slidesPerView={1.05}
              spaceBetween={16}
              breakpoints={{
                600: { slidesPerView: 2 },
                900: { slidesPerView: 3 },
                1200: { slidesPerView: 4 },
              }}
            >
              {products.map((p) => {
                const img = p.primaryImage ? buildImageUrl(p.primaryImage) : "";
                const frames = img ? [img] : [];

                return (
                  <SwiperSlide key={p.id}>
                    <ProductCard
                      product={p as any}
                      frames={frames}
                      active={activeId === p.id}
                      onActivate360={() => setActiveId(p.id)}
                      onDeactivate360={() => setActiveId(null)}
                      onOpenProduct={() =>
                        navigate(`/product/${p.slug}`, {
                          state: { product: p },
                        })
                      }
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>

            {/* CTA */}
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#111",
                  color: "#fff",
                  px: 5,
                  py: 1.6,
                  borderRadius: "999px",
                }}
                onClick={() => navigate("/products-listing?type=best-sellers")}
              >
                Explore More
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <DiscoverCampaigns />
    </>
  );
};

export default BestSellers;
